/**
 * FINAL PRODUCTION VERSION — supports ALL Shopify discount types
 * ---------------------------------------------------------------
 * Key principles:
 * - NO subtotal comparisons (invalid for many discount types)
 * - ONLY trust Shopify's discount_codes[] response
 * - Rebind after drawer refresh
 * - Ensure collapsible opens on error OR valid discount
 * - Display Shopify's real message when available
 */

if (!customElements.get('cart-discounts')) {
  class CartDiscounts extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.init();
    }

    init() {
      // Run after the DOM has settled following a drawer refresh
      setTimeout(() => this.bindEvents(), 0);
    }

    bindEvents() {
      const applyBtn = this.querySelector('.cart-discounts--button');
      const removeBtns = this.querySelectorAll('.cart-discounts--remove');

      if (applyBtn) {
        applyBtn.addEventListener('click', (e) => {
          e.preventDefault();
          applyBtn.classList.add('loading');
          this.applyCode();
        });
      }

      removeBtns.forEach((btn) =>
        btn.addEventListener('click', this.removeCode.bind(this))
      );
    }

    /* -------------------------------------------------------------
     * Helpers
     * ------------------------------------------------------------- */
    existingCodes() {
      return Array.from(this.querySelectorAll('.cart-discounts--name')).map((el) =>
        el.innerHTML.trim()
      );
    }

    getSectionsToRender() {
      return [
        { id: 'Cart', section: 'main-cart', selector: '.thb-cart-form' },
        { id: 'Cart-Drawer', section: 'cart-drawer', selector: '.cart-drawer' },
        { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-item-count' }
      ];
    }

    getSectionInnerHTML(html, selector) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const node = doc.querySelector(selector);
      return node ? node.innerHTML : html;
    }

    /* -------------------------------------------------------------
     * Apply Code
     * ------------------------------------------------------------- */
    applyCode() {
      const input = this.querySelector('.cart-discounts--input');
      const code = input?.value.trim();
      if (!code) return;

      const current = this.existingCodes();
      if (!current.includes(code)) current.push(code);

      this.updateDiscounts(current, code);
    }

    /* -------------------------------------------------------------
     * Remove Code
     * ------------------------------------------------------------- */
    removeCode(e) {
      const pill = e.target.previousElementSibling;
      const code = pill?.innerHTML.trim();
      if (!code) return;

      const current = this.existingCodes();
      const idx = current.indexOf(code);
      if (idx !== -1) current.splice(idx, 1);

      this.updateDiscounts(current);
    }

    /* -------------------------------------------------------------
     * Core Shopify Update Logic
     * ------------------------------------------------------------- */
    updateDiscounts(codes, attemptedCode = null) {
      const body = JSON.stringify({
        discount: codes.join(','),
        sections: this.getSectionsToRender().map((s) => s.section),
        sections_url: window.location.pathname
      });

      fetch(`${theme.routes.cart_update_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body
      })
        .then((r) => r.text())
        .then((txt) => {
          const parsed = JSON.parse(txt);
          const discounts = parsed.discount_codes || [];
          const attemptedLower = attemptedCode?.toLowerCase();

          /* -----------------------------------------
           * Update theme sections
           * ----------------------------------------- */
          this.getSectionsToRender().forEach((section) => {
            const root =
              document.getElementById(section.id)?.querySelector(section.selector) ||
              document.getElementById(section.id);

            if (parsed.sections && root) {
              root.innerHTML = this.getSectionInnerHTML(
                parsed.sections[section.section],
                section.selector
              );
            }
          });

          // Rebind discount logic on new DOM after render
          const fresh = document.querySelector('cart-discounts');
          if (fresh) fresh.init();

          /* -----------------------------------------
           * VALIDATION — TRUST SHOPIFY ONLY
           * ----------------------------------------- */

          let shopifyError = null;

          // Case 1 — Shopify returns no discounts at all
          if (attemptedCode && discounts.length === 0) {
            shopifyError = `Discount code "${attemptedCode}" is not valid.`;
          }

          // Case 2 — Shopify returns code but marks as not applicable
          const match = discounts.find(
            (d) => d.code?.toLowerCase() === attemptedLower && d.applicable === false
          );

          if (match) {
            shopifyError = match.description || `Discount code "${attemptedCode}" is not valid.`;
          }

          /* -----------------------------------------
           * SUCCESS CASE → keep discounts accordion OPEN
           * ----------------------------------------- */
          if (!shopifyError && attemptedCode) {
            setTimeout(() => {
              const container = document.querySelector('cart-discounts');
              const details = container?.closest('details');
              if (details && !details.hasAttribute('open')) details.setAttribute('open', '');
            }, 50);

            return; // Don't fall into error block
          }

          /* -----------------------------------------
           * ERROR CASE → show message + open accordion
           * ----------------------------------------- */
          if (shopifyError) {
            setTimeout(() => {
              const container = document.querySelector('cart-discounts');
              const errorEl = container?.querySelector('.cart-discounts--error');
              if (!errorEl) return;

              const details = errorEl.closest('details');
              if (details && !details.hasAttribute('open')) details.setAttribute('open', '');

              const textNode = errorEl.querySelector('.cart-discounts--error-text');
              if (textNode) textNode.textContent = shopifyError;

              errorEl.removeAttribute('hidden');
              errorEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }, 50);
          }
        })
        .catch((err) => console.error('Discount update failed:', err));
    }
  }

  customElements.define('cart-discounts', CartDiscounts);
}